import { ChangeEvent, useEffect, useRef, useState } from 'react';
import css from './FilterRateCardLane.module.scss';
import { Switch, TypeAhead, Select } from '@components/FORM';
import { Tooltip, Text } from '@components/CORE';
import { INPUT_ICON_POS, TOOLTIP_POSITION, TypeAheadOption } from '@types';
import { IconButton } from '@mui/material';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { useSearchRegion, searchPostcode } from '@utils/laneLookup';
import SearchIcon from '@mui/icons-material/Search';
import { debounce, statesAU } from '@utils';

const EmptyStop = {
	lane: undefined as TypeAheadOption | undefined,
	usePostcode: false,
	state: '',
};

type filterValue = { origin: typeof EmptyStop; destination: typeof EmptyStop };

interface FilterRateCardStopProps {
    stop: typeof EmptyStop;
    onChange: (stop: typeof EmptyStop) => void;
    placeholder?: string;
    label: string;
}

const FilterRateCardStop = ({ stop, onChange, label }: FilterRateCardStopProps) => {
	const searchRegion = useSearchRegion();
	const debouncedSearchRegion = useRef(debounce(searchRegion));
	const debouncedSearchPostcode = useRef(debounce(searchPostcode));
	const [value, setValue] = useState('');
	const [options, setOptions] = useState<TypeAheadOption[]>([]);
	const [loading, setLoading] = useState(false);
	const [states, setStates] = useState<Record<string, any>[]>([]);

	const lookupAddress = (e: ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		const getOptions = stop.usePostcode ? debouncedSearchPostcode.current : debouncedSearchRegion.current;

		setLoading(true);
		getOptions(e.target.value, stop.state)
			.then((opts) => {
				setOptions(opts);
			})
			.finally(() => {
				setLoading(false);
			})
	}

	useEffect(() => {
		setValue((stop.lane?.label || '') as string)
	}, [stop]);

	useEffect(() => {
		const auStates = statesAU.map((state) => {
			return {
				label: state.name,
				value: state.isoCode
			}
		});
		setStates(auStates);
	}, []);

	useEffect(() => {
		if (!stop.usePostcode && value) {
			setLoading(true);
			debouncedSearchRegion.current(value, stop.state)
				.then((opts) => {
					setOptions(opts);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [stop.state]);

	return (
		<div className={css.stop}>
			<label>{label}</label>
			<Switch
				name='usePostcode'
				onChange={() => {
					onChange({
						lane: undefined, usePostcode: !stop.usePostcode, state: ''
					});
				}}
				checked={stop.usePostcode}
				label='Use a Postcode'
				value=''
			/>
			{!stop.usePostcode && <Select
				label={'State'}
				name={'state'}
				value={stop.state}
				onChange={(value) => onChange({ ...stop, state: value as string })}
				placeholder={'Select a State or Territory'}
			>
				{states.map((state, i) => {
					return <Select.Option key={i} value={state.value}>{state.label}</Select.Option>
				})}
			</Select>}
			<TypeAhead
				label={stop.usePostcode ? 'Postcode' : 'Region'}
				name={'region'}
				value={value}
				onChange={(e) => lookupAddress(e)}
				keepErrorSpace={false}
				placeholder={stop.usePostcode ? 'Search for a postcode' : 'Search for a region'}
				Icon={SearchIcon}
				options={options}
				onSelected={(option) => {
					onChange({ ...stop, lane: option as TypeAheadOption });
					setValue((option as TypeAheadOption).label as string)
				}}
				isLoadingOptions={loading}
				iconPos={INPUT_ICON_POS.LEFT}
			/>
		</div>
	);
}

type FilterRateCardLaneProps = {
    header: any;
    meta: any;
    filterData: { value: string | undefined; option: filterValue } | undefined;
    onChange: (val: any) => void;
};

const FilterRateCardLane = ({ meta, filterData, onChange }: FilterRateCardLaneProps) => {
	const { origin = EmptyStop, destination = EmptyStop } = filterData?.option || {};
	const EmptyFilterData = {
		columnField: meta?.databaseLocation,
		operatorValue: 'lane',
	};

	const handleChanged = (newValue: filterValue | undefined) => {
		const laneId = [newValue?.origin, newValue?.destination]
			.map((stop) => {
				const code = stop?.usePostcode ? 'P' : 'R';
				return stop?.lane ? `${code}${stop.lane.value}` : '';
			})
			.join(':');
		const originState = newValue?.origin?.state || '';
		const destinationState = newValue?.destination?.state || '';
		const hasValue = laneId !== ':' || originState || destinationState;
		onChange({
			...(filterData || EmptyFilterData),
			value: hasValue ? {
				laneId, originState, destinationState
			} : undefined,
			option: newValue,
		});
	};

	const onSwapped = () => {
		handleChanged({ origin: destination, destination: origin });
	};

	const swapButton = (
		<Tooltip position={TOOLTIP_POSITION.BOTTOM} text='Swap origin and destination'>
			<IconButton onClick={onSwapped}>
				<SwapHorizOutlinedIcon />
				{!origin.usePostcode && <Text>Swap lanes</Text>}
			</IconButton>
		</Tooltip>
	);

	return (
		<div className={css.root}>
			<div className={css.withSwap}>
				<FilterRateCardStop
					onChange={(newOrigin) => handleChanged({ destination, origin: newOrigin })}
					placeholder='Pick-Up Location'
					stop={origin}
					label='Pick-up'
				/>
				{origin.usePostcode && swapButton}
			</div>
			{!origin.usePostcode && swapButton}
			<FilterRateCardStop
				onChange={(newDestination) => handleChanged({ origin, destination: newDestination })}
				placeholder='Drop-Off Location'
				stop={destination}
				label='Drop-off'
			/>
		</div>
	);
}

export default FilterRateCardLane;
